import { DateTime } from 'luxon';
import * as lineitems from 'smg/customer-api/lineitems';
import { OpenQuotes } from 'smg/customer-api/v2/quotes';
import * as models from 'smg/customer-api/models';
import { HumanFileSize } from 'smg/formatting';
import * as html from 'smg/html';
import { IsImageURL, Picture } from 'smg/picture';
import QueryString from 'smg/querystring';
import { SanitizeRichHTML } from 'smg/richtext';
import * as template from 'smg/template';
import { DrawQuoteOrOrderCartItem } from '../carts';
import { Linkify } from '../linkify';

main();

function main() {
    const $spinnerContainer = document.querySelector('#quote-search-spinner-container') as HTMLElement;
    const $searchContainer = document.querySelector('#quote-search-container') as HTMLElement;
    const $resultsContainer = document.querySelector('#quote-search-results-container') as HTMLElement;
    const $results = document.querySelector('#quote-search-results') as HTMLElement;

    const $search = document.querySelector('#quote-search-input') as HTMLInputElement;

    const currQuery = QueryString().get('q');
    let q: string = currQuery || '';
    const quoteID = QueryString().get('id');
    const quoteSentInstanceID = QueryString().get('instance');

    const showSearch = () => {
        if (currQuery) {
            $search.value = currQuery;
        }

        $searchContainer.classList.remove('d-none');
        $spinnerContainer.remove();
    };

    if (quoteID) {
        if (quoteID.length === 43) { // key
            q = `key:${quoteID}`;
        } else {
            q = `id:${quoteID}`;
        }
    } else if (quoteSentInstanceID) {
        if (quoteSentInstanceID.length === 43) { // key
            q = `quoteSentKey:${quoteSentInstanceID}`;
        } else {
            q = `quoteSentInstanceID:${quoteSentInstanceID}`;
        }
    } else if (!q) {
        showSearch();
        return;
    }

    OpenQuotes({
        sort: 'created asc',
        max: 100,
        data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteOrders,quoteRequest,key,quoteSents',
        q,
    }).then(async data => {
        if (!data.results.length) {
            if (currQuery) {
                const emptySearchWarning = document.querySelector('#empty-search-warning') as HTMLElement;
                emptySearchWarning.classList.remove('d-none');
            }

            showSearch();
            return;
        }

        const f = document.createDocumentFragment();

        const { groups, unsentQuotes } = groupQuotesByQuoteSentInstance(data.results);

        for (let i = 0; i < groups.length; i++) {
            const group = groups[i];

            // Every group after the first gets a dated boundary marker so the
            // send instances never run together. The first boundary (newest →
            // older) stays prominent to mark the transition.
            if (i > 0) {
                f.appendChild(renderGroupBoundary(group.latestQuoteSent.created, i === 1));
            }

            const $section = document.createElement('section');
            $section.classList.add('quote-group');
            if (i === 0) {
                $section.classList.add('quote-group-latest');
            }

            renderQuoteSentMessage(group.latestQuoteSent, $section, i > 0);

            for (const quote of group.quotes) {
                await appendQuoteCard(quote, $section);
            }

            f.appendChild($section);
        }

        if (unsentQuotes.length > 0) {
            if (groups.length > 0) {
                f.appendChild(renderGroupBoundary(undefined, false, 'Not Yet Sent'));
            }

            const $section = document.createElement('section');
            $section.classList.add('quote-group', 'quote-group-unsent');

            for (const quote of unsentQuotes) {
                await appendQuoteCard(quote, $section);
            }

            f.appendChild($section);
        }

        $spinnerContainer.remove();
        $results.appendChild(f);
        $resultsContainer.classList.remove('d-none');
    }).catch(err => {
        showSearch();
        throw err;
    });

    void lineitems.GetLineItems();

    if (QueryString().get('ref') === 'search') {
        const $backToSearch = document.querySelector('#back-to-search-container') as HTMLElement;
        $backToSearch.classList.remove('d-none');
    }
}

interface quoteGroup {
    latestQuoteSent: models.QuoteSent;
    latestMs: number;
    quotes: models.Quote[];
}

// Group every quote by the quote-sent instance of its most recent quote-sent,
// so each send's message renders above the quotes that send carried. Groups
// sort newest send first; quotes never sent render last with no message card.
// This page is where emailed/texted quote links land (the company detail
// v3QuoteViewURL), and many sends are SMS-only, so it's the only place the
// customer ever sees the rep's message.
function groupQuotesByQuoteSentInstance(quotes: models.Quote[]): { groups: quoteGroup[]; unsentQuotes: models.Quote[] } {
    const groupsByInstanceID = new Map<string, quoteGroup>();
    const unsentQuotes: models.Quote[] = [];

    for (const quote of quotes) {
        const quoteSent = latestQuoteSentForQuote(quote);
        if (!quoteSent) {
            unsentQuotes.push(quote);
            continue;
        }

        const ms = quoteSent.created?.isValid ? quoteSent.created.toMillis() : 0;
        // Very old quote-sents predate instances — fall back to the row's own id.
        const instanceID = quoteSent.quoteSentInstanceID || quoteSent.id || '';

        const group = groupsByInstanceID.get(instanceID);
        if (group) {
            group.quotes.push(quote);
            if (ms > group.latestMs) {
                group.latestMs = ms;
                group.latestQuoteSent = quoteSent;
            }
        } else {
            groupsByInstanceID.set(instanceID, {
                latestQuoteSent: quoteSent,
                latestMs: ms,
                quotes: [quote],
            });
        }
    }

    const groups = [...groupsByInstanceID.values()];
    groups.sort((a, b) => b.latestMs - a.latestMs);

    return { groups, unsentQuotes };
}

function latestQuoteSentForQuote(quote: models.Quote): models.QuoteSent | undefined {
    let latest: models.QuoteSent | undefined;
    let latestMs = -1;
    for (const quoteSent of quote.quoteSents) {
        const ms = quoteSent.created?.isValid ? quoteSent.created.toMillis() : 0;
        if (ms > latestMs) {
            latestMs = ms;
            latest = quoteSent;
        }
    }

    return latest;
}

async function appendQuoteCard(quote: models.Quote, parent: HTMLElement): Promise<void> {
    // Build the CartItem directly — no reason to serialize the whole quote
    // through JSON just to re-hydrate it; DrawQuoteOrOrderCartItem accepts a
    // hydrated CartItem as-is.
    const cartItem = new models.CartItem();
    cartItem.quote = quote;
    cartItem.subtotal = quote.cachedTotal;
    cartItem.total = quote.cachedTotal;
    const $card = await DrawQuoteOrOrderCartItem({
        cartItemInit: cartItem,
        disableDelete: true,
        addToCart: true,
    });
    if (!$card) {
        return;
    }

    parent.appendChild($card);
}

// The boundary marker between two send-instance groups: lines flanking a
// timeline-style node labeled with the following group's send date, so every
// section boundary is unmistakable even when a group has no message card.
function renderGroupBoundary(sent: DateTime | undefined, prominent: boolean, overrideLabel?: string): DocumentFragment {
    const clone = template.Clone('#quote-group-boundary-template');

    const dateText = sent?.isValid ? sent.toLocaleString(DateTime.DATE_MED) : '';

    let label: string;
    if (overrideLabel) {
        label = overrideLabel;
    } else if (prominent) {
        label = dateText ? `Earlier Quotes · Sent ${dateText}` : 'Earlier Quotes';
    } else {
        label = dateText ? `Sent ${dateText}` : 'Earlier Quotes';
    }

    const $text = clone.querySelector('.quote-group-boundary-text') as HTMLElement;
    $text.textContent = label;
    $text.classList.add(prominent ? 'fs-5' : 'fs-6');

    return clone;
}

// The message card for one quote-send, rendered above its group's quote cards.
// The most recent group keeps the full prominent card; older groups render a
// subtler variant. Same card as the account quote page. A send with no message
// body still gets a card (header + attachments) when the rep attached files.
function renderQuoteSentMessage(quoteSent: models.QuoteSent, parent: HTMLElement, subtle: boolean): void {
    const content = quoteSent.content?.trim() ?? '';
    const subject = quoteSent.subject?.trim() ?? '';
    const attachments = quoteSent.attachments.filter(attachment => attachment.publicURL);
    // Subject-only sends still render a header card — the subject often
    // carries the send's intent (e.g. preliminary vs action needed).
    if (!content && !subject && attachments.length === 0) {
        return;
    }

    const clone = template.Clone('#quote-sent-message-template');

    const repFirstName = quoteSent.user?.firstName?.trim() ?? '';
    (clone.querySelector('.quote-sent-kicker') as HTMLElement).textContent = repFirstName ?
        `A message from ${repFirstName}` :
        'A message from our team';

    if (content || subject) {
        (clone.querySelector('.quote-sent-title') as HTMLElement).textContent = subject || 'Your Quote Is Ready';
    } else {
        (clone.querySelector('.quote-sent-title') as HTMLElement).remove();
    }
    if (content) {
        const $body = clone.querySelector('.quote-sent-body') as HTMLElement;
        if (quoteSent.contentType === 'text/html') {
            $body.innerHTML = Linkify(SanitizeRichHTML(content));
        } else {
            $body.innerHTML = Linkify(html.Nl2br(html.Escape(content)));
        }
    } else {
        (clone.querySelector('.quote-sent-body') as HTMLElement).remove();
    }

    const $date = clone.querySelector('.quote-sent-date') as HTMLElement;
    if (quoteSent.created?.isValid) {
        $date.textContent = quoteSent.created.toLocaleString(DateTime.DATETIME_MED);
    } else {
        $date.remove();
    }

    if (attachments.length > 0) {
        renderQuoteSentAttachments(attachments, clone, `quote-sent-${quoteSent.quoteSentInstanceID || quoteSent.id || ''}`);
    }

    if (subtle) {
        const $card = clone.querySelector('.quote-sent-message') as HTMLElement;
        $card.classList.add('shadow-none');
        $card.classList.replace('py-6', 'py-4');

        const $iconMark = clone.querySelector('.icon-mark') as HTMLElement;
        $iconMark.classList.remove('icon-mark-secondary');

        const $kicker = clone.querySelector('.quote-sent-kicker') as HTMLElement;
        $kicker.classList.replace('text-primary', 'text-body-tertiary');

        const $title = clone.querySelector('.quote-sent-title') as HTMLElement | null;
        if ($title) {
            $title.classList.replace('fs-4', 'fs-5');
            $title.classList.replace('fs-sm-3', 'fs-sm-4');
            $title.classList.replace('fw-800', 'fw-700');
        }
    }

    parent.appendChild(clone);
}

function isImageAttachment(attachment: models.QuoteSentAttachment): boolean {
    return IsImageURL(attachment.name || attachment.publicURL);
}

// The files the rep attached to the send's email. Images render as thumbnails
// opening in fancybox (grouped per send instance); anything else renders as a
// compact file row opening in a new tab.
function renderQuoteSentAttachments(attachments: models.QuoteSentAttachment[], clone: DocumentFragment, fancyboxGroup: string): void {
    const $wrap = clone.querySelector('.quote-sent-attachments') as HTMLElement;
    $wrap.classList.remove('d-none');

    const $images = clone.querySelector('.quote-sent-attachment-images') as HTMLElement;
    const $files = clone.querySelector('.quote-sent-attachment-files') as HTMLElement;

    for (const attachment of attachments) {
        const url = attachment.publicURL;
        if (!url) {
            continue;
        }

        const name = attachment.name.trim() || 'Attachment';

        if (isImageAttachment(attachment)) {
            const $thumb = template.Clone('#quote-sent-attachment-image-template');
            const $anchor = $thumb.querySelector('a') as HTMLAnchorElement;
            const $img = $anchor.querySelector('img') as HTMLImageElement;
            $anchor.href = url;
            $anchor.setAttribute('data-fancybox', fancyboxGroup);
            $anchor.setAttribute('data-caption', name);
            void Picture($img, url, { displayWidth: 96, alt: name, title: name, fancyboxThumb: true });
            $images.appendChild($thumb);
        } else {
            const $file = template.Clone('#quote-sent-attachment-file-template');
            const $anchor = $file.querySelector('a') as HTMLAnchorElement;
            $anchor.href = url;
            $anchor.textContent = name;

            const $size = $file.querySelector('.quote-sent-attachment-file-size') as HTMLElement;
            if (attachment.size) {
                $size.textContent = HumanFileSize(attachment.size);
            } else {
                $size.remove();
            }

            $files.appendChild($file);
        }
    }

    if (!$images.hasChildNodes()) {
        $images.remove();
    }
    if (!$files.hasChildNodes()) {
        $files.remove();
    }
}